import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { getToken, API } from "../lib/api";
import BrandHeader from "../components/BrandHeader";
import BrandFooter from "../components/BrandFooter";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import ClientContactsCard from "../components/ClientContactsCard";
import ConfirmDialog from "../components/ConfirmDialog";
import { ChevronLeft, RefreshCw, CloudUpload, Loader2, Download, FileText, Bell, Pencil, Activity, Users, Plus, Trash2, FileDown, Globe, Calendar, Palette, Save, Eye, Printer, ChevronDown } from "lucide-react";
import { toast } from "sonner";

function WhiteLabelEditor({ onb, onSaved }) {
  const [label, setLabel] = useState(onb.msp_label || "");
  const [color, setColor] = useState(onb.msp_color || "");
  const [reminderDate, setReminderDate] = useState(onb.next_reminder_at ? String(onb.next_reminder_at).slice(0, 10) : "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/onboardings/${onb.id}`, {
        msp_label: label, msp_color: color,
        next_reminder_at: reminderDate || null,
      });
      toast.success("Onboarding settings saved");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <Card className="bg-white border-slate-200 p-6 mt-6 shadow-sm" data-testid="whitelabel-panel">
      <h3 className="font-semibold mb-1 text-brand-primary flex items-center gap-2"><Palette size={16}/>Branding & schedule</h3>
      <p className="text-xs text-slate-500 mb-4">Optionally co-brand this onboarding (e.g., show the outgoing MSP's name alongside Sentry) and schedule the next reminder.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label className="text-slate-800">Co-brand label</Label>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g., Acme IT Services" data-testid="whitelabel-label-input"/>
          <p className="text-xs text-slate-500">Shown next to Sentry in the wizard header.</p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-800">Co-brand accent color</Label>
          <div className="flex gap-2 items-center">
            <Input value={color} onChange={(e) => setColor(e.target.value)} placeholder="#3F51B5" className="font-mono text-sm" data-testid="whitelabel-color-input"/>
            {color && <div className="w-9 h-9 rounded border border-slate-300 shrink-0" style={{ background: color }}/>}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-800 inline-flex items-center gap-1"><Calendar size={14}/>Next reminder date</Label>
          <Input type="date" value={reminderDate} onChange={(e) => setReminderDate(e.target.value)} data-testid="next-reminder-input"/>
          <p className="text-xs text-slate-500">Overrides the global cadence. Leave blank to use cadence.</p>
        </div>
      </div>
      <div className="flex justify-end mt-4">
        <Button onClick={save} disabled={saving} className="font-semibold" style={{ background: "#0F1644", color: "#fff" }} data-testid="whitelabel-save-btn">
          {saving ? <Loader2 className="animate-spin" size={14}/> : <><Save size={14} className="mr-1.5"/>Save</>}
        </Button>
      </div>
    </Card>
  );
}

export default function AdminOnboardingView() {  const { id } = useParams();
  const nav = useNavigate();
  const [onb, setOnb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newDomain, setNewDomain] = useState("");
  const [adding, setAdding] = useState(false);
  const [pendingRemoveUser, setPendingRemoveUser] = useState(null);
  const [pendingRemoveDomain, setPendingRemoveDomain] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get(`/onboardings/${id}`),
        api.get(`/sections`),
      ]);
      setOnb(a.data.onboarding); setSections(b.data.sections);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/onboardings/${id}/sync-itglue`);
      const s = data.summary || {};
      const bits = [];
      if (s.created) bits.push(`${s.created} new`);
      if (s.updated) bits.push(`${s.updated} updated`);
      if (s.unchanged) bits.push(`${s.unchanged} unchanged`);
      const warn = (s.warn || 0) + (s.error || 0);
      if (warn) bits.push(`${warn} warning(s)`);
      const label = bits.length ? bits.join(" · ") : "complete";
      toast.success(`ITGlue sync ${label}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const downloadFile = async (fid, filename) => {
    const res = await fetch(`${API}/admin/files/${fid}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = async () => {
    try {
      const res = await fetch(`${API}/onboardings/${id}/pdf`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error("pdf");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `Onboarding-${onb.company_name.replace(/\s/g, "_")}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("PDF generation failed"); }
  };

  const downloadPrintPdf = async (blank = false) => {
    try {
      const res = await fetch(`${API}/onboardings/${id}/pdf/print?blank=${blank ? "true" : "false"}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error("pdf");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `Onboarding-${onb.company_name.replace(/\s/g, "_")}-${blank ? "blank" : "print"}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(blank ? "Blank form PDF downloaded" : "Print preview PDF downloaded");
    } catch { toast.error("PDF generation failed"); }
  };

  const addUser = async (e) => {
    e?.preventDefault();
    if (!newUserEmail) return;
    setAdding(true);
    try { await api.post(`/onboardings/${id}/users`, { email: newUserEmail }); toast.success(`Invited ${newUserEmail}`); setNewUserEmail(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setAdding(false); }
  };
  const removeUser = async (email) => {
    try { await api.delete(`/onboardings/${id}/users/${encodeURIComponent(email)}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const addDomain = async (e) => {
    e?.preventDefault();
    if (!newDomain) return;
    setAdding(true);
    try { await api.post(`/onboardings/${id}/domains`, { domain: newDomain }); toast.success(`Authorized ${newDomain}`); setNewDomain(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setAdding(false); }
  };
  const removeDomain = async (d) => {
    try { await api.delete(`/onboardings/${id}/domains/${encodeURIComponent(d)}`); toast.success("Domain removed"); load(); }
    catch { toast.error("Failed"); }
  };

  const renderValue = (v) => {
    if (v === null || v === undefined || v === "") return <span className="text-slate-400">—</span>;
    if (Array.isArray(v)) return <span>{v.join(", ")}</span>;
    if (typeof v === "object") return <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(v, null, 2)}</pre>;
    return <span>{String(v)}</span>;
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="animate-spin text-brand-primary"/></div>;
  if (!onb) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <BrandHeader showLogout>
        <Button onClick={() => setUsersOpen(true)} variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10" data-testid="users-btn">
          <Users size={14} className="mr-2"/>Users
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10" data-testid="pdf-menu-btn">
              <FileDown size={14} className="mr-2"/>PDF<ChevronDown size={12} className="ml-1 opacity-70"/>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuItem onClick={downloadPdf} data-testid="pdf-kickoff-item">
              <FileDown size={14} className="mr-2"/>
              <div className="flex flex-col">
                <span className="font-medium">Kickoff packet</span>
                <span className="text-xs text-slate-500">Submitted answers, no blanks</span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => downloadPrintPdf(false)} data-testid="pdf-print-item">
              <Printer size={14} className="mr-2"/>
              <div className="flex flex-col">
                <span className="font-medium">Print preview</span>
                <span className="text-xs text-slate-500">Current answers + blank lines</span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => downloadPrintPdf(true)} data-testid="pdf-blank-item">
              <Printer size={14} className="mr-2"/>
              <div className="flex flex-col">
                <span className="font-medium">Blank form</span>
                <span className="text-xs text-slate-500">For hand-filling offline</span>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button onClick={async () => { try { await api.post(`/onboardings/${id}/remind`); toast.success("Reminder sent"); load(); } catch(e) { toast.error(e?.response?.data?.detail || "Failed"); } }} variant="ghost" disabled={onb?.status==="submitted"} className="text-white/80 hover:text-white hover:bg-white/10" data-testid="remind-btn">
          <Bell size={14} className="mr-2"/>Remind
        </Button>
        <Button onClick={() => nav(`/admin/onboarding/${id}/edit`)} variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10" data-testid="edit-fill-btn">
          <Pencil size={14} className="mr-2"/>Fill
        </Button>
        <Button onClick={sync} disabled={syncing || onb.status !== "submitted"} className="font-semibold" style={{ background: "#00C7FF", color: "#0F1644" }} data-testid="sync-itglue-btn">
          {syncing ? <Loader2 className="animate-spin mr-2" size={14}/> : <CloudUpload size={14} className="mr-2"/>}Sync to ITGlue
        </Button>
      </BrandHeader>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-4">
          <button onClick={() => nav("/admin")} className="text-sm text-slate-600 hover:text-brand-primary inline-flex items-center gap-1" data-testid="back-btn"><ChevronLeft size={14}/>Back to dashboard</button>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-brand-primary">{onb.company_name}</h1>
            <p className="text-slate-600 mt-1">{onb.client_email} · Status: <span className="text-slate-800 font-medium">{onb.status}</span></p>
          </div>
          {onb.itglue_org_id && <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-md">ITGlue org: {onb.itglue_org_id}</div>}
        </div>

        {onb.status !== "submitted" && (
          <Card className="bg-brand-primary border-brand-accent/30 p-5 mb-6 text-white" data-testid="fill-as-client-cta">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-lg flex items-center gap-2"><Pencil size={18}/>Help fill out {onb.client_name || "the client"}&apos;s onboarding</div>
                <p className="text-sm text-white/80 mt-1">Two modes for working with this client&apos;s data:</p>
                <ul className="text-sm text-white/85 mt-2 space-y-1 ml-4 list-disc marker:text-brand-accent">
                  <li><strong>Preview as client</strong> — read-only, shows exactly what the client sees. Great for sales demos &amp; walkthroughs.</li>
                  <li><strong>Fill on behalf</strong> — same wizard, fully editable. Great for kickoff calls &amp; clients who&apos;d rather have you fill it.</li>
                </ul>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                <Button onClick={() => nav(`/admin/onboarding/${onb.id}/preview`)} variant="outline" className="border-violet-300 text-violet-100 bg-violet-500/10 hover:bg-violet-500/20 font-semibold whitespace-nowrap" data-testid="preview-as-client-btn">
                  <Eye size={14} className="mr-1.5"/>Preview as client
                </Button>
                <Button onClick={() => nav(`/admin/onboarding/${onb.id}/edit`)} className="font-semibold whitespace-nowrap" style={{ background: "#00C7FF", color: "#0F1644" }} data-testid="fill-as-client-btn">
                  <Pencil size={14} className="mr-1.5"/>Fill on behalf →
                </Button>
              </div>
            </div>
          </Card>
        )}

        <ClientContactsCard onb={onb} onChange={load}/>

        {onb.activity?.length > 0 && (
          <Card className="bg-white border-slate-200 p-5 mb-6 shadow-sm" data-testid="activity-panel">
            <div className="flex items-center gap-2 mb-3"><Activity size={16} className="text-brand-primary"/><div className="font-medium text-brand-primary">Activity timeline</div></div>
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {onb.activity.slice().reverse().map((a, i) => (
                // Activity rows are append-only — `at` timestamps are essentially
                // unique. Composite key handles same-second events safely.
                <div key={`${a.at}-${a.event}-${i}`} className="flex items-start gap-3 text-xs text-slate-700 border-l-2 border-slate-200 pl-3 py-1">
                  <span className="text-slate-400 whitespace-nowrap">{new Date(a.at).toLocaleString()}</span>
                  <span className="text-slate-900 font-mono">{a.event}</span>
                  {a.meta && Object.keys(a.meta).length > 0 && <span className="text-slate-500">{JSON.stringify(a.meta)}</span>}
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-slate-200 text-xs text-slate-500 flex gap-4 flex-wrap">
              <span>Reminders sent: <span className="text-slate-700">{onb.reminder_count || 0}</span></span>
              {onb.last_reminder_at && <span>Last: <span className="text-slate-700">{new Date(onb.last_reminder_at).toLocaleString()}</span></span>}
              {onb.first_opened_at && <span>First opened: <span className="text-slate-700">{new Date(onb.first_opened_at).toLocaleString()}</span></span>}
            </div>
          </Card>
        )}

        {onb.files?.length > 0 && (
          <Card className="bg-white border-slate-200 p-5 mb-6 shadow-sm">
            <div className="font-medium mb-3 text-brand-primary">Attached files</div>
            <div className="space-y-2">
              {onb.files.map(f => (
                <div key={f.id} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded p-3" data-testid={`file-${f.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText size={16} className="text-brand-primary shrink-0"/>
                    <span className="truncate text-sm">{f.filename}</span>
                    <span className="text-xs text-slate-500">{(f.size/1024).toFixed(1)} KB</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => downloadFile(f.id, f.filename)} className="border-slate-300"><Download size={14}/></Button>
                </div>
              ))}
            </div>
          </Card>
        )}

        <div className="space-y-6">
          {sections.map(s => {
            const sd = onb.data?.[s.id] || {};
            return (
              <Card key={s.id} className="bg-white border-slate-200 p-6 shadow-sm" data-testid={`section-view-${s.id}`}>
                <h2 className="text-xl font-semibold mb-4 text-brand-primary">{s.title}</h2>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                  {s.fields?.map(f => (
                    <div key={f.key}>
                      <dt className="text-slate-500 text-xs uppercase tracking-wider">{f.label}</dt>
                      <dd className="text-slate-900 mt-0.5">{renderValue(sd[f.key])}</dd>
                    </div>
                  ))}
                  {s.extra_fields?.map(f => (
                    <div key={f.key}>
                      <dt className="text-slate-500 text-xs uppercase tracking-wider">{f.label}</dt>
                      <dd className="text-slate-900 mt-0.5">{renderValue(sd[f.key])}</dd>
                    </div>
                  ))}
                </dl>
                {s.repeatable_key && Array.isArray(sd[s.repeatable_key]) && sd[s.repeatable_key].length > 0 && (
                  <div className="mt-5 pt-5 border-t border-slate-200">
                    <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">{s.repeatable_label}s ({sd[s.repeatable_key].length})</div>
                    <div className="space-y-2">
                      {sd[s.repeatable_key].map((row, i) => (
                        // Prefer the stable per-row UUID assigned by ClientWizard's
                        // RepeatableRows. Legacy rows pre-dating that change fall
                        // back to a composite of section + index.
                        <div key={row.__rowKey || `${s.repeatable_key}-${i}`} className="bg-slate-50 border border-slate-200 rounded p-3 text-sm grid grid-cols-2 md:grid-cols-4 gap-2">
                          {Object.entries(row).filter(([k]) => k !== "__rowKey").map(([k, v]) => <div key={k}><span className="text-slate-500">{k}:</span> <span className="text-slate-900">{String(v||"—")}</span></div>)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {onb.itglue_sync_log?.length > 0 && (() => {
          const last = onb.itglue_sync_log[onb.itglue_sync_log.length - 1] || {};
          const entries = last.entries || [];
          const cov = entries.find((e) => e.step === "section_coverage") || null;
          const tally = { created: 0, updated: 0, unchanged: 0, warn: 0, error: 0, found: 0, deleted_prior: 0, info: 0, skipped: 0 };
          entries.forEach((e) => {
            if (tally[e.status] !== undefined) tally[e.status]++;
          });
          return (
            <Card className="bg-white border-slate-200 p-6 mt-6 shadow-sm" data-testid="itglue-sync-log-card">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <h3 className="font-semibold text-brand-primary">ITGlue sync log</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Last sync: {new Date(last.at).toLocaleString()}
                    {onb.itglue_org_id ? <> · ITGlue org #{onb.itglue_org_id}</> : null}
                  </p>
                </div>
                <Button onClick={sync} disabled={syncing} size="sm" className="font-semibold" style={{ background: "#00C7FF", color: "#0F1644" }} data-testid="resync-itglue-btn">
                  {syncing ? <Loader2 className="animate-spin" size={14}/> : "Re-sync (incremental)"}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mb-3 text-xs">
                {tally.created > 0 && <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">+{tally.created} created</span>}
                {tally.updated > 0 && <span className="px-2 py-1 rounded-full bg-sky-50 text-sky-700 border border-sky-200">~{tally.updated} updated</span>}
                {tally.unchanged > 0 && <span className="px-2 py-1 rounded-full bg-slate-50 text-slate-600 border border-slate-200">={tally.unchanged} unchanged</span>}
                {(tally.warn + tally.error) > 0 && <span className="px-2 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200">{tally.warn + tally.error} warning(s)</span>}
                {cov && <span className="px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">{cov.synced?.length || 0}/{cov.total || 16} sections</span>}
              </div>
              {cov && cov.skipped?.length ? (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3">
                  <span className="font-medium">Sections with no data: </span>{cov.skipped.join(", ")}
                </div>
              ) : null}
              <details className="text-xs">
                <summary className="cursor-pointer text-slate-600 hover:text-brand-primary select-none mb-2">View detailed sync log ({entries.length} steps)</summary>
                <pre className="bg-slate-50 border border-slate-200 p-3 rounded overflow-x-auto text-slate-700">{JSON.stringify(onb.itglue_sync_log, null, 2)}</pre>
              </details>
            </Card>
          );
        })()}

        <WhiteLabelEditor onb={onb} onSaved={load} />
      </main>
      <BrandFooter />

      <Dialog open={usersOpen} onOpenChange={setUsersOpen}>
        <DialogContent className="bg-white text-slate-900 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-brand-primary">Authorized users & domains</DialogTitle>
            <DialogDescription>Add additional people (client team, outgoing MSP) or trusted domains. They'll be able to sign in via magic link and contribute to this onboarding.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Users ({(onb.authorized_emails || []).length})</div>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {(onb.authorized_emails || []).map(em => (
                  <div key={em} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded p-2.5 text-sm" data-testid={`user-row-${em}`}>
                    <span className="truncate">
                      {em}
                      {em === onb.client_email && <span className="ml-2 text-xs px-1.5 py-0.5 rounded text-brand-primary" style={{ background: "rgba(0,199,255,0.15)" }}>Primary</span>}
                    </span>
                    {em !== onb.client_email && <Button size="sm" variant="ghost" onClick={() => setPendingRemoveUser(em)} className="text-rose-600 hover:bg-rose-50" data-testid={`remove-user-${em}`}><Trash2 size={14}/></Button>}
                  </div>
                ))}
              </div>
              <form onSubmit={addUser} className="flex gap-2 mt-3">
                <Input type="email" value={newUserEmail} onChange={(e)=>setNewUserEmail(e.target.value)} placeholder="user@company.com" required data-testid="add-user-input"/>
                <Button type="submit" disabled={adding} className="font-semibold" style={{ background: "#00C7FF", color: "#0F1644" }} data-testid="add-user-submit">
                  {adding ? <Loader2 className="animate-spin" size={14}/> : <><Plus size={14} className="mr-1"/>Invite</>}
                </Button>
              </form>
            </div>

            <div className="pt-3 border-t border-slate-200">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><Globe size={12}/>Trusted domains ({(onb.authorized_domains || []).length})</div>
              <p className="text-xs text-slate-500 mb-2">Any email at these domains may request a magic link for this onboarding. Use sparingly.</p>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {(onb.authorized_domains || []).map(d => (
                  <div key={d} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded p-2.5 text-sm" data-testid={`domain-row-${d}`}>
                    <span>@{d}</span>
                    <Button size="sm" variant="ghost" onClick={() => setPendingRemoveDomain(d)} className="text-rose-600 hover:bg-rose-50" data-testid={`remove-domain-${d}`}><Trash2 size={14}/></Button>
                  </div>
                ))}
              </div>
              <form onSubmit={addDomain} className="flex gap-2 mt-3">
                <Input type="text" value={newDomain} onChange={(e)=>setNewDomain(e.target.value)} placeholder="company.com" required data-testid="add-domain-input"/>
                <Button type="submit" disabled={adding} variant="outline" className="border-slate-300" data-testid="add-domain-submit">
                  {adding ? <Loader2 className="animate-spin" size={14}/> : <><Plus size={14} className="mr-1"/>Add</>}
                </Button>
              </form>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!pendingRemoveUser}
        onOpenChange={(v) => !v && setPendingRemoveUser(null)}
        title="Remove user?"
        description={pendingRemoveUser ? `Remove ${pendingRemoveUser} from this onboarding? They'll no longer be able to sign in via magic-link to fill out the form.` : ""}
        confirmLabel="Remove user"
        destructive
        onConfirm={() => pendingRemoveUser && removeUser(pendingRemoveUser)}
        testId="remove-user-confirm"
      />

      <ConfirmDialog
        open={!!pendingRemoveDomain}
        onOpenChange={(v) => !v && setPendingRemoveDomain(null)}
        title="Remove authorized domain?"
        description={pendingRemoveDomain ? `Remove @${pendingRemoveDomain} from the authorized domains list? Users from this domain will lose the ability to sign in to this onboarding.` : ""}
        confirmLabel="Remove domain"
        destructive
        onConfirm={() => pendingRemoveDomain && removeDomain(pendingRemoveDomain)}
        testId="remove-domain-confirm"
      />
    </div>
  );
}
